import { useEffect, useRef, useState, useCallback, isValidElement, Children, FC } from 'react';

import { FocusTrap } from 'focus-trap-react';

import { noScroll } from './Modal.css';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalBody } from './ModalBody';
import { ModalContainer } from './ModalContainer';
import { ModalContext } from './ModalContext';
import { ModalFooter } from './ModalFooter';
import { ModalHeader } from './ModalHeader';
import { ModalProps } from './types';
import { Colors, sprinkles, VDS_ZINDEX } from '../../tokens';
import { Button, IconButton } from '../Button';
import { Flex } from '../Flex';
import { IconXLine } from '../Icon';
import { Text } from '../Text';

const ModalRoot: React.FC<ModalProps> = ({
  open,
  onClose,
  size = 400,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  children,
  className,
  transitionDuration = 0,
  // Props 기반 구성
  title,
  closable = true,
  showConfirmButton = true,
  showCancelButton = true,
  showPrevButton = false,
  showLeftDeleteButton = false,
  showRightDeleteButton = false,
  footer,
  onConfirm,
  onCancel,
  onPrev,
  onDelete,
  confirmText = '확인',
  cancelText = '취소',
  prevText = '이전',
  deleteText = '삭제',
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // children이 compound component인지 확인
  const hasCompoundComponents = useCallback(() => {
    let hasHeader = false;
    let hasBody = false;
    let hasFooter = false;

    Children.forEach(children ?? [], (child) => {
      if (isValidElement(child)) {
        const childType = child.type as FC<unknown>;
        const displayName = childType?.displayName;
        if (displayName === 'ModalHeader') hasHeader = true;
        if (displayName === 'ModalBody') hasBody = true;
        if (displayName === 'ModalFooter') hasFooter = true;
      }
    });

    return hasHeader || hasBody || hasFooter;
  }, [children]);

  // 모달이 열릴 때 이전 포커스를 저장하고 배경 스크롤을 차단
  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.classList.add(noScroll);
      setShouldRender(true);
      setIsExiting(false);
    } else if (shouldRender) {
      // 닫힐 때 애니메이션 시작
      setIsExiting(true);

      const timer = setTimeout(() => {
        setShouldRender(false);
        document.body.classList.remove(noScroll);

        // 이전 포커스 위치로 복원
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      }, transitionDuration);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [open, shouldRender, transitionDuration]);

  // ESC 키 핸들러
  useEffect(() => {
    if (!open || disableEscapeKeyDown) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, disableEscapeKeyDown, onClose]);

  // 백드롭 클릭 핸들러
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (disableBackdropClick) return;

      // 백드롭만 클릭했을 때만 닫기
      if (event.target === event.currentTarget) {
        onClose?.();
      }
    },
    [disableBackdropClick, onClose],
  );

  // 포커스 가능한 첫 번째 요소를 찾는 함수
  const findFirstFocusableElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    const focusableQuerySelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // 바디 영역에서 포커스 가능한 요소 찾기
    const bodyElement = containerRef.current.querySelector('[class*="vds-modal-body"]');
    if (bodyElement) {
      const focusableInBody = bodyElement.querySelectorAll<HTMLElement>(focusableQuerySelector);
      if (focusableInBody.length > 0) {
        return focusableInBody[0];
      }
    }

    // 바디에 없으면 푸터에서 찾기 (CTA 버튼, 주로 마지막 버튼)
    const footerElement = containerRef.current.querySelector('[class*="vds-modal-footer"]');
    if (footerElement) {
      const focusableInFooter = footerElement.querySelectorAll<HTMLElement>(focusableQuerySelector);
      if (focusableInFooter.length > 0) {
        // 푸터의 마지막 버튼 (일반적으로 CTA 버튼)
        return focusableInFooter[focusableInFooter.length - 1];
      }
    }

    // 그 외의 경우 컨테이너 자체에 포커스
    return containerRef.current;
  }, []);

  // 모달이 열릴 때 첫 번째 포커스 가능한 요소로 포커스 이동
  useEffect(() => {
    if (open && !isExiting && shouldRender && containerRef.current) {
      // 약간의 딜레이를 주어 DOM이 완전히 렌더링되도록 함
      const timer = setTimeout(() => {
        const firstFocusable = findFirstFocusableElement();
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 50);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [open, isExiting, shouldRender, findFirstFocusableElement]);

  if (!shouldRender) return null;

  // Compound component 사용 여부 확인
  const isCompoundComponent = hasCompoundComponents();

  // Props 기반 자동 구성 모달 렌더링
  const renderPropsBasedModal = () => {
    const handleConfirmClick = () => {
      onConfirm?.();
      if (!onConfirm) {
        onClose?.();
      }
    };

    const handleCancelClick = () => {
      onCancel?.();
      onClose?.();
    };

    const handlePrevClick = () => {
      onPrev?.();
    };

    const handleDeleteClick = () => {
      onDelete?.();
    };

    return (
      <>
        {(title || closable) && (
          <ModalHeader>
            <Flex justify="between" gap={8}>
              <div className={sprinkles({ paddingTop: 8 })}>
                {typeof title === 'string' ? <Text textStyle="20_600">{title}</Text> : title}
              </div>
              {closable && (
                <IconButton
                  buttonType="ghost"
                  onClick={onClose}
                  icon={<IconXLine width={20} height={20} fill={Colors.gray900} />}
                  tooltipMessage="닫기"
                />
              )}
            </Flex>
          </ModalHeader>
        )}
        {children && <ModalBody className="vds-modal-body">{children}</ModalBody>}
        {footer !== null &&
          (showConfirmButton ||
            showCancelButton ||
            showPrevButton ||
            showLeftDeleteButton ||
            showRightDeleteButton) && (
            <ModalFooter className="vds-modal-footer">
              {footer != undefined ? (
                footer
              ) : (
                <>
                  {showLeftDeleteButton && (
                    <Button buttonType="danger" buttonSize="l" onClick={handleDeleteClick}>
                      {deleteText}
                    </Button>
                  )}
                  <div style={{ flex: 1 }} />
                  {showCancelButton && (
                    <Button buttonType="border" buttonSize="l" onClick={handleCancelClick}>
                      {cancelText}
                    </Button>
                  )}
                  {showPrevButton && (
                    <Button buttonType="normal" buttonSize="l" onClick={handlePrevClick}>
                      {prevText}
                    </Button>
                  )}
                  {showConfirmButton && (
                    <Button buttonType="primary" buttonSize="l" onClick={handleConfirmClick}>
                      {confirmText}
                    </Button>
                  )}
                  {showRightDeleteButton && (
                    <Button buttonType="danger" buttonSize="l" onClick={handleDeleteClick}>
                      {deleteText}
                    </Button>
                  )}
                </>
              )}
            </ModalFooter>
          )}
      </>
    );
  };

  return (
    <ModalContext.Provider value={{ zIndex: VDS_ZINDEX.MODAL + 1 }}>
      <FocusTrap
        active={open && !isExiting}
        focusTrapOptions={{
          initialFocus: false, // 수동으로 포커스 관리
          allowOutsideClick: true,
          escapeDeactivates: false, // ESC는 별도로 처리
          returnFocusOnDeactivate: false, // 수동으로 포커스 복원
        }}
      >
        <div>
          <ModalBackdrop onClick={handleBackdropClick} isExiting={isExiting} />
          <ModalContainer
            ref={containerRef}
            size={size}
            className={className}
            isExiting={isExiting}
          >
            {isCompoundComponent ? children : renderPropsBasedModal()}
          </ModalContainer>
        </div>
      </FocusTrap>
    </ModalContext.Provider>
  );
};

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
  Container: ModalContainer,
  Backdrop: ModalBackdrop,
});
